import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Calendar, 
  Clock, 
  User, 
  ChevronRight,
  Plus
} from 'lucide-react';
import { sessions, patients } from '../services/api';

const Sessions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState('all');
  const navigate = useNavigate();
  const [sessionsList, setSessionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Sample data - will be replaced with API call later
  const patients = [
    { id: 'all', name: 'Todos os Pacientes' },
    { id: 'PT-12345', name: 'Alex Johnson' },
    { id: 'PT-12346', name: 'Sam Williams' },
    { id: 'PT-12347', name: 'Riley Garcia' }
  ];

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        console.log('Fetching sessions...');
        const response = await sessions.getAll();
        console.log('Sessions response:', response);
        
        // Fetch patient details for each session
        const sessionsWithPatients = await Promise.all(
          response.data.map(async (session) => {
            if (!session.patientId) {
              return session;
            }
            try {
              const patientResponse = await patients.getById(session.patientId);
              return {
                ...session,
                patient: patientResponse.data
              };
            } catch (error) {
              console.error(`Error fetching patient for session ${session.id}:`, error);
              return session;
            }
          })
        );
        
        setSessionsList(sessionsWithPatients);
      } catch (error) {
        console.error('Error fetching sessions:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  // Filter sessions based on search term and selected patient
  const filteredSessions = sessionsList.filter(session => {
    const patientName = session.patient ? 
      `${session.patient.firstName} ${session.patient.lastName}`.toLowerCase() : '';
    const matchesSearch = 
      patientName.includes(searchTerm.toLowerCase()) ||
      (session.type || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPatient = selectedPatient === 'all' || session.patientId === selectedPatient;
    return matchesSearch && matchesPatient;
  });

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sessões</h1>
          <p className="text-gray-600">Gerenciar e visualizar todas as sessões de terapia</p>
        </div>
        <button className="flex items-center px-4 py-2 bg-brand-700 text-white rounded-lg text-sm font-medium hover:bg-brand-800">
          <Plus size={16} className="mr-2" />
          Nova Sessão
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar sessões..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-lg w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={selectedPatient}
                onChange={(e) => setSelectedPatient(e.target.value)}
                className="border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-4 text-center text-gray-500">Carregando sessões...</div>
          ) : filteredSessions.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchTerm ? 'Nenhuma sessão encontrada para sua busca' : 'Nenhuma sessão disponível'}
            </div>
          ) : (
            filteredSessions.map((session) => (
              <div key={session.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="bg-brand-100 rounded-lg p-3 text-brand-700">
                      <Calendar size={20} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-medium">{session.type || 'Terapia ABA'}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          session.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-brand-100 text-brand-800'
                        }`}>
                          {(session.status?.charAt(0).toUpperCase() + session.status?.slice(1)) || 'N/A'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{session.date || 'N/A'} às {session.time || 'N/A'}</p>
                      <div className="flex items-center mt-1">
                        <User size={14} className="text-gray-400 mr-1" />
                        <span className="text-sm text-gray-600">
                          {session.patient ? 
                            `${session.patient.firstName} ${session.patient.lastName}` : 
                            'Paciente Desconhecido'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-500">{session.duration ? `${session.duration} minutos` : 'N/A'}</span>
                    <button 
                      onClick={() => navigate(`/dashboard/patients/${session.patientId}/sessions/${session.id}`)}
                      className="text-brand-600 hover:text-brand-800 flex items-center"
                    >
                      Ver Detalhes
                      <ChevronRight size={16} className="ml-1" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Sessions; 